import React from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../app/redux/useAppSelector';
import LoadingIndicatorModal from '../components/LoadingIndicatorModal';
import { MenuItem } from '../components/Menu';
import { gql } from '../graphql/generated';
import attachmentIcon from '../icons/non-prefilled/16x16/attachment.svg';
import GoogleDrivePicker from '../search/GoogleDrivePicker';
import { UIActions } from '../ui/uiSlice';
import { isElectron } from '../utils/desktopClient';
import openPickFileDialog from '../utils/openPickFileDialog';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';

const IMPORT_FILES_MUTATION = gql(/** @complexity 8 */ `
  mutation createFileWithImport($files: [Upload!]!, $topicId: ID) {
    importFiles(files: $files, isDraft: true, topicId: $topicId) {
      post {
        publishedAt
        ...postPath_post
      }
    }
  }
`);

const FILE_UPLOAD_MENU_OPTIONS = [
  {
    name: 'Markdown',
    extensions: '.md',
  },
  {
    name: 'HTML',
    extensions: '.text/html,.html,.htm',
  },
  {
    name: 'Docx',
    extensions: '.docx',
  },
];

interface ImportSubmenuProps {
  topicId?: string;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const ImportSubmenuItems = ({ topicId }: ImportSubmenuProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [importFiles] = useMutation(IMPORT_FILES_MUTATION);

  const handleFileUpload = (files: FileList | null) => {
    if (!files) {
      return;
    }

    const dismissModal = () => dispatch(UIActions.closeModal());
    dispatch(
      UIActions.openModal(LoadingIndicatorModal, {
        disableOutsideClose: true,
      }),
    );

    importFiles({
      variables: {
        files: Array.from(files),
        topicId: topicId === '' ? undefined : topicId,
      },
    })
      .then(({ data }) => {
        if (data?.importFiles[0]) {
          const [{ post }] = data.importFiles;
          dismissModal();
          navigate(postPath(post));
        }
      })
      .catch(dismissModal);
  };

  const handleGoogleDriveImport = () => {
    dispatch(
      UIActions.openModal(GoogleDrivePicker, { disableOutsideClose: true }),
    );
  };

  return (
    <>
      {FILE_UPLOAD_MENU_OPTIONS.map((option) => (
        <MenuItem
          key={option.name}
          onClick={() => {
            openPickFileDialog({
              extensions: option.extensions,
              onFilesPicked: handleFileUpload,
            });
          }}
        >
          {option.name}
        </MenuItem>
      ))}
      {!isElectron && (
        <MenuItem onClick={handleGoogleDriveImport}>Drive</MenuItem>
      )}
    </>
  );
};

interface ImportMenuButtonProps {
  topicId?: string;
}

const ImportMenuButton = (props: ImportMenuButtonProps) => {
  return (
    <MenuItem
      icon={attachmentIcon}
      submenuItems={<ImportSubmenuItems {...props} />}
    >
      Import
    </MenuItem>
  );
};

export default ImportMenuButton;
